import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { useLeaderboard } from '../hooks/useLeaderboard'
import { LeaderboardRow } from './LeaderboardRow'
import type { LeaderboardScope } from '../types'

const SCOPES: readonly LeaderboardScope[] = ['friends', 'global']

/**
 * Classement par XP, à portée choisie (spécifications § 6.4). Le classement
 * global ne montre que des pseudos : jamais le nom réel.
 */
export function Leaderboard() {
  const { t } = useTranslation()
  const [scope, setScope] = useState<LeaderboardScope>('friends')
  const { data: entries, isLoading, isError } = useLeaderboard(scope)

  return (
    <section aria-labelledby="social-leaderboard-title" className="flex flex-col gap-4">
      <h2
        id="social-leaderboard-title"
        className="font-display text-lg font-semibold text-on-surface"
      >
        {t('social.leaderboard.title')}
      </h2>

      <div role="tablist" aria-label={t('social.leaderboard.title')} className="flex gap-2">
        {SCOPES.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            id={`leaderboard-tab-${value}`}
            aria-selected={scope === value}
            aria-controls="leaderboard-panel"
            onClick={() => setScope(value)}
            className={[
              'min-h-11 rounded-full px-4 text-sm font-semibold transition-colors',
              scope === value
                ? 'bg-primary text-white'
                : 'bg-surface-low text-on-surface-variant hover:bg-surface-container',
            ].join(' ')}
          >
            {t(`social.leaderboard.scopes.${value}`)}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id="leaderboard-panel"
        aria-labelledby={`leaderboard-tab-${scope}`}
        className="flex flex-col gap-2"
      >
        {isError && <Alert variant="error">{t('social.leaderboard.load_error')}</Alert>}

        {isLoading && (
          <div className="flex flex-col gap-2">
            {['sk-1', 'sk-2', 'sk-3'].map((id) => (
              <div key={id} className="h-16 animate-pulse rounded-2xl bg-surface-container" />
            ))}
          </div>
        )}

        {entries && entries.length === 0 && (
          <p className="rounded-2xl bg-surface-low px-4 py-6 text-center text-sm text-on-surface-variant">
            {t('social.leaderboard.empty')}
            <span className="mt-1 block text-on-surface-variant">
              {t('social.leaderboard.empty_hint')}
            </span>
          </p>
        )}

        {entries && entries.length > 0 && (
          <ul className="flex flex-col gap-2">
            {entries.map((entry) => (
              <LeaderboardRow key={entry.user_id} entry={entry} />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
